import React, {
  useRef,
  useImperativeHandle,
  forwardRef,
  useState,
  useLayoutEffect,
  useCallback
} from "react";
import { defineMessage } from "@lingui/macro";
import PropTypes from "kf-proptypes/widgets";
import dayjs from "dayjs";
import { Show } from "control-flow-react";

import { FIELD_TYPES } from "base/constants";
import { i18n } from "base/i18n";
import { formatDate, formatDateTime } from "base/utils";

import {
  FloatingMenu,
  FLOATING_MENU_POSITION
} from "../../inputs/dropdown/floatingmenu.jsx";
import { MenuItem } from "../../inputs/dropdown/menu.jsx";
import { Link } from "../../navigation/link/index.jsx";
import {
  ICON_SIZE,
  ICON_STROKE,
  KF_ICONS,
  KfIcon
} from "@widgets/common/components/primitives/kf_icon/index.jsx";

import styles from "./style.css";

export {
  getSuggestionList,
  hasSmartAttachmentFeature,
  canUseSuggestions,
  resolveCurrencyType
} from "../../common/libs/document.parser.service.js";

const GROUPED_SUGGESTION_HEADER = defineMessage`AI suggestion from {filename}`;

function groupSuggestions(data = []) {
  const filteredData = data.filter((item) => item.value !== undefined);
  return filteredData.reduce((suggestion, item) => {
    const { suggestedBy, ...val } = item;
    const key = i18n._(GROUPED_SUGGESTION_HEADER, {
      filename: suggestedBy
    });
    suggestion[key] = suggestion[key] || [];

    if (!suggestion[key].some((existing) => val.value === existing.value)) {
      suggestion[key].push(val);
    }

    return suggestion;
  }, {});
}

function getPosition(container, itemsLength = 0, fieldType) {
  function getTop() {
    const element =
      fieldType === FIELD_TYPES.TEXT_AREA ? '[class*="textArea"]' : "input";
    const inputElement = container.querySelector(element);
    const [top, parentTop] = [
      container.getBoundingClientRect().top,
      container.offsetParent.getBoundingClientRect().top
    ];
    const [labelHeight, inputHeight] = [
      inputElement ? inputElement.offsetTop - container.offsetTop : 20,
      inputElement?.offsetHeight || 36
    ];
    const [bottomSpacing, topSpacing, itemHeight] = [20, 0, 46];
    const inputTop = top + labelHeight + inputHeight - parentTop;
    const parentContainer =
      inputElement?.closest('[class*="contentWrapper"]') || document.body;
    const docHeight = parentContainer.getBoundingClientRect().height;
    const dropdownHeight = Math.min(itemHeight * itemsLength, 240);
    if (
      docHeight >
      inputTop + dropdownHeight + labelHeight + inputHeight + bottomSpacing
    ) {
      return inputTop + topSpacing;
    } else {
      return top - parentTop - topSpacing - dropdownHeight - inputHeight;
    }
  }
  const top = getTop();
  return {
    top,
    left: container.offsetLeft
  };
}
function SuggestionMenu(
  {
    onSelect,
    suggestionList = [],
    hasBlur = true,
    container,
    value = "",
    fieldType = ""
  },
  ref
) {
  const [position, setPosition] = useState({});
  let floatRef = useRef(null);
  const updatePosition = useCallback(
    function updatePosition() {
      if (container && container.offsetParent) {
        setPosition(getPosition(container, suggestionList?.length, fieldType));
      }
    },
    [container, suggestionList, fieldType]
  );
  function hideMenu() {
    floatRef.current?.hide();
  }
  function showMenu() {
    updatePosition();
    floatRef.current?.show();
  }

  useImperativeHandle(ref, () => ({
    show: showMenu,
    hide: hideMenu,
    previous: () => {
      floatRef.current?.previous();
    },
    next: () => {
      floatRef.current?.next();
    },
    select: () => {
      floatRef.current?.select();
    },
    onKeyDown: (keyEvent, { Type }, canHandleEvents = true) => {
      if (canHandleEvents) {
        floatRef.current?.show();
        if (keyEvent.key === "ArrowDown") {
          floatRef.current?.next();
        } else if (keyEvent.key === "ArrowUp") {
          floatRef.current?.previous();
        } else if (keyEvent.key === "Enter") {
          floatRef.current?.select();
        } else if (keyEvent.key === "Tab") {
          hideMenu();
        }
      }
    }
  }));
  useLayoutEffect(
    function updatePositionOnFilter() {
      updatePosition();
    },
    [suggestionList, updatePosition]
  );

  function handleSelect(item) {
    onSelect(item.value, item.meta);
    hideMenu();
  }

  return (
    suggestionList?.length > 0 && (
      <FloatingMenu
        ref={floatRef}
        dataSource={groupSuggestions(suggestionList)}
        className={styles.floating}
        emptyRenderer={() => null}
        position={FLOATING_MENU_POSITION.BOTTOM_LEFT_OVER}
        top={position.top ? position.top : "auto"}
        left={"auto"}
        closeOnOutsideClick={true}
        hideOnScroll={true}
        onSelect={(item) =>
          setTimeout(() => {
            onSelect();
            handleSelect(item);
          }, 100)
        }
        isGrouped={true}
        itemRenderer={(item) => {
          let selectedValue = String(value);
          if (fieldType === FIELD_TYPES.CURRENCY && selectedValue) {
            selectedValue = selectedValue.replace(/[^\d.]/g, "");
          }
          const isActive = Boolean(
            String(item.value) === String(selectedValue)
          );
          return (
            <MenuItem
              isSelected={isActive}
              key={item.value}
              onMouseDown={(e) => (hasBlur ? handleSelect(item) : () => {})}
            >
              <div className={styles.menuItem}>
                <div>{item.value}</div>
                <div className={styles.selectedIconContainer}>
                  <Show when={isActive}>
                    <KfIcon
                      icon={KF_ICONS.CHECKMARK}
                      size={ICON_SIZE.SIZE_16}
                      stroke={ICON_STROKE.REGULAR}
                      color="--kf-color-secondary-one-500"
                    />
                  </Show>
                </div>
              </div>
            </MenuItem>
          );
        }}
        getContainer={() => container || document.body}
      />
    )
  );
}

SuggestionMenu.propTypes = {
  ref: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }),
  container: PropTypes.shape({ offsetParent: PropTypes.element }),
  onSelect: PropTypes.func.isRequired,
  suggestionList: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.string,
  hasBlur: PropTypes.bool,
  fieldType: PropTypes.string
};

export const DocumentSuggestions = forwardRef(SuggestionMenu);

export function DateSuggestionsHeader({ suggestionList }) {
  let currentUser = window.accountDetails.UserDetails;
  let userDateFormat = currentUser.DateFormat;
  // eslint-disable-next-line react/prop-types
  return ({ onDateChange, closePopup }) => {
    function handleSelect(dateStr) {
      onDateChange(dayjs(new Date(dateStr)), true, closePopup);
    }
    const suggestion = groupSuggestions(suggestionList);

    return (
      Object.keys(suggestion).length > 0 && (
        <div className={styles.pickerHeaderWrapper}>
          {Object.keys(suggestion).map((filename) => (
            <div key={filename}>
              <div className={`${styles.pickerHeaderTitle} tiny`}>
                <p className={`tiny-600 ${styles.dateSuggestionHeader}`}>
                  {filename}
                </p>
              </div>
              <div className={`${styles.pickerHeaderContent} tiny`}>
                {suggestion[filename].map(({ value: item }, index) => (
                  <Link
                    key={index}
                    className={styles.quickSelectLink}
                    onClick={() => handleSelect(item)}
                  >
                    {formatDate(item, userDateFormat)}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )
    );
  };
}
export function DateTimeSuggestionsHeader({ suggestionList }) {
  let currentUser = window.accountDetails.UserDetails;
  let userDateFormat = currentUser.DateFormat;
  // eslint-disable-next-line react/prop-types
  return ({ onDateChange, closePopup }) => {
    function handleSelect(dateTimeStr) {
      onDateChange(dayjs(new Date(dateTimeStr)), true, closePopup);
    }
    const suggestion = groupSuggestions(suggestionList);
    return (
      Object.keys(suggestion).length > 0 && (
        <div className={styles.pickerHeaderWrapper}>
          {Object.keys(suggestion).map((filename) => (
            <div key={filename}>
              <div className={`${styles.pickerHeaderTitle} tiny`}>
                <p className={`tiny-600 ${styles.dateSuggestionHeader}`}>
                  {filename}
                </p>
              </div>
              <div className={`${styles.pickerHeaderContent} tiny`}>
                {suggestion[filename].map(({ value: item }, index) => (
                  <Link
                    key={index}
                    className={styles.quickSelectLink}
                    onClick={() => handleSelect(item)}
                  >
                    {formatDateTime(item, userDateFormat)}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )
    );
  };
}
